import { Config } from "@planetscale/database";
import { and, eq, isNull } from "drizzle-orm";
import { Link, useNavigate } from "react-router";
import { formatDate } from "~/components/site/Date";
import { LinkButton } from "~/components/site/links/LinkButton";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { pg } from "~/pg.server";
import { useRealm, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, callApi, OurLoaderArgs } from "~/util/Loaders";
import { logPageView } from "~/util/stats";
import { generateUrl, getTitle, wikiPage } from "~/util/wiki";
import { editPageApi } from "../api/mdPages.pages";
import { Button } from "~/components/ui/Button";
import { useState } from "react";
import { PencilIcon } from "~/components/site/icons/Pencil";

const TITLE_PAGE = wikiPage;

async function makeData(args: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(args);
  //
  const pages = await pg.db
    .select({
      id: pg.mdPages.id,
      slug: pg.mdPages.wikiSlug,
      game: pg.mdPages.game,
      createdAt: pg.mdPages.createdAt,
      revision: {
        id: pg.mdRevisions.id,
        titleEn: pg.mdRevisions.titleEn,
        contentEn: pg.mdRevisions.contentEn,
        titleRu: pg.mdRevisions.titleRu,
        contentRu: pg.mdRevisions.contentRu,
        createdAt: pg.mdRevisions.createdAt,
      },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
    .where(
      and(
        eq(pg.mdPages.isDeleted, false),
        isNull(pg.mdPages.npcName),
        isNull(pg.mdPages.itemName),
        isNull(pg.mdPages.locName),
        isNull(pg.mdPages.questId),
        isNull(pg.mdPages.skillId),
        isNull(pg.mdPages.draftId)
      )
    );
  return { pages, authUser: args.req.sess?.user };
}

$PAGE({
  path: "wiki",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => {
    const { t } = cx;
    return { title: `${t(wikiPage)} - L2Hub.info` };
  },
  render: ({ data }) => {
    const { pages, authUser } = data;
    const t = useT();
    const turl = useTurl();
    const realm = useRealm();
    const nav = useNavigate();
    const pageTitle = t(TITLE_PAGE);
    const [loading, setLoading] = useState(false);

    async function createDraftPage(userId: number) {
      setLoading(true);
      const result = await callApi(editPageApi, {
        input: {
          game: [],
          target: { _: "draft" },
          titleEn: "Default Title",
          titleRu: "Без заголовка",
        },
        realm,
      });

      switch (result.t) {
        case "success":
          setLoading(false);
          return nav(`/users/${userId}/pages/${result.data.result.id}/edit`);
        case "error":
          setLoading(false);
          break;
      }
    }
    return (
      <div>
        <div className="content">
          <Breadcrumb
            items={[
              {
                title: t(TITLE_PAGE),
                url: turl("/wiki"),
              },
            ]}
          />
          <PageTitle>{pageTitle}</PageTitle>
          {authUser && (
            <div style={{ marginBottom: 20, marginLeft: 10, width: 200 }}>
              <Button
                onClick={() => createDraftPage(authUser.id)}
                loading={loading}
              >
                <PencilIcon width={20} height={20} />
                Add Draft
              </Button>
            </div>
          )}
        </div>
        <Table
          data={pages}
          id={(page) => page.id}
          cols={[
            {
              title: t({ en: "Title", ru: "Название" }),
              cell: (page) => (
                <Link to={turl("/wiki/" + generateUrl(page.id, page.slug))}>
                  {t(getTitle(page.revision.titleEn, page.revision.titleRu))}
                </Link>
              ),
            },
            {
              title: t({ en: "Last Update", ru: "Последнее обновление" }),
              cell: (page) => formatDate(page.revision.createdAt),
            },
          ]}
        />
      </div>
    );
  },
});
